import * as React from "react";
import { FormComponent } from "../../../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { Alphanumeric } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { NationalInsuranceNumber } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { MifirCountryIdentifierClient } from "../../../../../../../Wrap/Api/Regulations/MifirCountryIdentifier";
import { TaxReferenceType } from "../../../../../../../Wrap/Constants/Enums/Customer/TaxReferenceType";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { MifirIdentifierType } from "../../../../../../../Wrap/Constants/Enums/Domain/MifirIdentifierType";
import { MifirCountryIdentifier } from "../../../../../../../Wrap/Models/Domains/Regulations/MifirCountryIdentifier";
import { CustomerTaxResidencyModel } from "../../../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { CountryCodeMap } from "../../../../../../Shared/Entities/Maps/CountryCodeMap";
import { ValidatorContents } from "../../../../../Common/ValidatorHelper";
import { NationalIdentifierPair } from "../../../NewBusinessWizard/Components/FullClientDetail/NationalIdentifierPair";
import { ClientRecordAction } from "../../Action/ClientRecordAction";
import { RegulatoryTabs } from "../../Resource/RegulatoryContents";
import {
    RegulatoryHelper,
    TaxReferenceTypeMap_GBR,
    TaxReferenceTypeMap_SGP,
    TaxReferenceTypeMap_USA,
} from "../../Resource/RegulatoryCountryHelper";
import { TableViewSection } from "../TableViewSection";
import { UserClaim } from "../../../../../../../Wrap/Constants/Enums/UserClaim";
import { checkPermission } from "../../../../../../Shared/Permission/PermissionHelper";
import { Lightbox as NewLightbox } from "../../../../../../../react-core/Html/Components/Lightbox";
import { ControllingPersonRoleDetailModel } from "../../../../../../../Wrap/Models/Models/Customer/ControllingPersonRoleDetailModel";
import { RoleStatus } from "../../../../../../../Wrap/Constants/Enums/RoleStatus";
import { ControllingPersonType } from "../../../../../../../Wrap/Constants/Enums/Customer/ControllingPersonType";

interface ICustomerTaxResidencyTableProps {
    customerid: number;
    data: Array<CustomerTaxResidencyModel>;
    reloadTable: () => void;
    hasReadWriteAccess: boolean;
    controllingPersonData: Array<ControllingPersonRoleDetailModel>;
    needReload: () => void;
}
interface ICustomerTaxResidencyTableState {
    isLoading: boolean;
    mifirCountryIdentifiers: Map<CountryCode, MifirCountryIdentifier[]>;
    isEdit: boolean;
    showDeleteLightbox: boolean;
    showLastDeleteLightbox: boolean;
    itemToRemove: number;
}
export class CustomerTaxResidencyTable extends FormComponent<
    ICustomerTaxResidencyTableProps,
    ICustomerTaxResidencyTableState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
            isEdit: false,
            mifirCountryIdentifiers: new Map<CountryCode, MifirCountryIdentifier[]>(),
            showDeleteLightbox: false,
            showLastDeleteLightbox: false,
            itemToRemove: null,
        };
    }

    private child = null;

    componentDidMount() {
        this.getData();
    }

    getData() {
        this.setState({ isLoading: true });
        if (this.props.data) {
            this.props.data.map((x) => x.Country && this.loadMifirCountryIdentifier(x.Country));
            this.setState({
                isLoading: false,
            });
        }
    }

    // return true if this country is requiring tax reference as nationality number
    private countryHasTaxReferenceIdentifier(isTaxResident: boolean, country: CountryCode): boolean {
        const { mifirCountryIdentifiers } = this.state;
        return (
            country &&
            isTaxResident &&
            mifirCountryIdentifiers.has(country) &&
            mifirCountryIdentifiers.get(country)[0].IdentifierType == MifirIdentifierType.TaxNumber
        );
    }

    private getNationalIdentifier(
        country: CustomerTaxResidencyModel
    ): { nationalIdentifierType: string; nationalIdentifier: string } {
        const { mifirCountryIdentifiers } = this.state;
        if (country?.IsNationality) {
            if (country?.Country == CountryCode.GBR && country?.TaxReference) {
                if (country?.TaxReferenceType == TaxReferenceType.GbrUtr) {
                    return {
                        nationalIdentifierType: "Unique Taxpayer Reference",
                        nationalIdentifier: country?.TaxReference,
                    };
                } else {
                    return {
                        nationalIdentifierType: "National Insurance Number",
                        nationalIdentifier: country?.TaxReference,
                    };
                }
            } else {
                const countryIdentifiers = mifirCountryIdentifiers.get(country?.Country);
                if (country?.NationalIdentifier) {
                    if (
                        countryIdentifiers?.length > 0 &&
                        countryIdentifiers.some((x) => x?.IdentifierType == MifirIdentifierType.NationalIdentifier)
                    ) {
                        return {
                            nationalIdentifierType: countryIdentifiers?.find(
                                (x) => x?.IdentifierType == MifirIdentifierType.NationalIdentifier
                            )?.IdentifierTypeWording,
                            nationalIdentifier: country?.NationalIdentifier,
                        };
                    }
                    return {
                        nationalIdentifierType: "Nationality identifier",
                        nationalIdentifier: country?.NationalIdentifier,
                    };
                } else if (country?.PassportNumber) {
                    if (
                        countryIdentifiers?.length > 0 &&
                        countryIdentifiers?.some((x) => x?.IdentifierType == MifirIdentifierType.PassportNumber)
                    ) {
                        return {
                            nationalIdentifierType: countryIdentifiers?.find(
                                (x) => x?.IdentifierType == MifirIdentifierType.PassportNumber
                            )?.IdentifierTypeWording,
                            nationalIdentifier: country?.PassportNumber,
                        };
                    }
                    return { nationalIdentifierType: "Passport", nationalIdentifier: country?.PassportNumber };
                } else if (country?.TaxReference && country?.TaxReference != "") {
                    if (
                        countryIdentifiers?.length > 0 &&
                        countryIdentifiers?.some((x) => x?.IdentifierType == MifirIdentifierType.TaxNumber)
                    ) {
                        return {
                            nationalIdentifierType: countryIdentifiers?.find(
                                (x) => x?.IdentifierType == MifirIdentifierType.TaxNumber
                            )?.IdentifierTypeWording,
                            nationalIdentifier: country?.TaxReference,
                        };
                    }
                }
            }
        }
        return { nationalIdentifierType: "", nationalIdentifier: "" };
    }

    render() {
        const {
            isLoading,
            showDeleteLightbox,
            showLastDeleteLightbox,
            itemToRemove,
            mifirCountryIdentifiers,
        } = this.state;
        const { data } = this.props;
        if (isLoading || mifirCountryIdentifiers.size < data?.length) {
            return <PartLoading isLoading={true} />;
        }

        const columnConfigs = [
            { title: "Country name", key: "Country", render: (x) => <span>{CountryCodeMap.getString(x)}</span> },
            { title: "Client nationality", key: "IsNationality", render: (x) => <span>{x ? "Yes" : "No"}</span> },
            { title: "Client tax resident", key: "IsTaxResident", render: (x) => <span>{x ? "Yes" : "No"}</span> },
            { title: "Primary citizenship", key: "PrimaryCitizenship", render: (x) => <span>{x ? "Yes" : "No"}</span> },
            {
                title: "Nationality identifier",
                key: "NationalIdentifier",
                render: (x, row) => {
                    return this.getNationalIdentifier(row)?.nationalIdentifier;
                },
            },
            {
                title: "Nationality identifier type",
                render: (x, row) => {
                    return this.getNationalIdentifier(row)?.nationalIdentifierType;
                },
            },
            {
                title: "Tax identification number",
                key: "TaxReference",
                render: (x, row) => {
                    // return row?.IsTaxResident || (row?.IsNationality && row?.TaxReference) ? row?.TaxReference : "";
                    return row?.IsTaxResident ? row?.TaxReference : "";
                },
            },
            {
                title: "Tax identification number type",
                key: "TaxReferenceType",
                render: (x, row) => (
                    <span>
                        {x && row?.IsTaxResident
                            ? mifirCountryIdentifiers
                                  ?.get(row?.Country)
                                  ?.find(
                                      (x) =>
                                          x?.IdentifierType == MifirIdentifierType.TaxNumber &&
                                          x?.Country == row?.Country
                                  ) != null
                                ? mifirCountryIdentifiers
                                      ?.get(row?.Country)
                                      ?.find(
                                          (x) =>
                                              x?.IdentifierType == MifirIdentifierType.TaxNumber &&
                                              x?.Country == row?.Country
                                      )?.IdentifierTypeWording
                                : "Tax identification number"
                            : null}
                    </span>
                ),
            },
        ];
        return (
            <>
                <TableViewSection
                    sectionHeader={RegulatoryTabs.CustomerTaxResidency}
                    columnConfigs={columnConfigs}
                    lightboxContent={(data, onDataChange, isEdit) => this.renderLightBox(data, onDataChange, isEdit)}
                    onSave={(data) => this.onSave(data)}
                    onAdd={(data) => this.onAdd(data)}
                    canAdd={
                        data?.length < 6 &&
                        this.props.hasReadWriteAccess == true &&
                        checkPermission(UserClaim.AdviserPortalCustomerFullyAccessCanView)
                    }
                    canRemove={this.props.hasReadWriteAccess == true}
                    onRemove={(data) => {
                        this.setState({ showDeleteLightbox: true, itemToRemove: data });
                        return true;
                    }}
                    data={data}
                    canEdit={
                        //TODO Change later
                        checkPermission(UserClaim.AdviserPortalCustomerFullyAccessCanView) &&
                        this.props.hasReadWriteAccess == true
                    }
                />
                {showDeleteLightbox && (
                    <NewLightbox
                        cssWrapper="is-default-lightbox is-delete-lightbox"
                        show={true}
                        onClose={() => this.setState({ showDeleteLightbox: false })}
                        title="Delete filter"
                    >
                        <p>Are you sure you want to delete?</p>
                        <div className="row row-center">
                            <div className="col-auto">
                                <button
                                    type="button"
                                    className="btn secondary"
                                    onClick={() => this.setState({ showDeleteLightbox: false })}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="col-auto">
                                <button
                                    type="button"
                                    className="btn primary"
                                    onClick={() => {
                                        this.onRemove(itemToRemove);
                                        this.setState({ showDeleteLightbox: false });
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </NewLightbox>
                )}
                {showLastDeleteLightbox && (
                    <NewLightbox
                        cssWrapper="is-default-lightbox is-delete-lightbox"
                        show={true}
                        onClose={() => this.setState({ showLastDeleteLightbox: false })}
                        title="Delete filter"
                    >
                        <p>Client must have at least one nationality</p>
                        <div className="row row-center">
                            <div className="col-auto">
                                <button
                                    type="button"
                                    className="btn secondary"
                                    onClick={() => this.setState({ showLastDeleteLightbox: false })}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </NewLightbox>
                )}
            </>
        );
    }

    renderLightBox(data: CustomerTaxResidencyModel, onDataChange, isEdit): JSX.Element {
        return (
            <>
                <SelectEditor
                    options={CountryCodeMap.getArray()}
                    getText={(v) => {
                        return CountryCodeMap.getString(v);
                    }}
                    placeholder="Please select"
                    label={"Country name"}
                    required="Please select country"
                    selected={data?.Country}
                    cssWrapper="control-size-430"
                    onChange={(v) => {
                        onDataChange({
                            Country: v,
                            IsTaxResident: v == CountryCode.GBR ? true : null,
                            PrimaryTaxResidency: null,
                            PrimaryCitizenship: null,
                            NationalIdentifier: null,
                            PassportNumber: null,
                            TaxReferenceType: null,
                            TaxReference: null,
                            TaxResidencyForPensionRelief: null,
                            TaxExempt: null,
                            TaxExemptionReason: null,
                            IsNationality: null,
                        });
                        this.loadMifirCountryIdentifier(v);
                    }}
                    disabled={isEdit}
                    validationRules={[
                        {
                            errorMessage: "Country already exists",
                            validator: (v) => isEdit || this.validateCountry(v),
                        },
                        {
                            errorMessage: "Please add UK as the first tax resident country",
                            validator: (v) => {
                                if (v && v == CountryCode.GBR) {return true;}
                                return (this.props.data || []).some((ele) => ele.Country == CountryCode.GBR);
                            },
                        },
                    ]}
                />
                {((data?.Country != null && this.validateCountry(data?.Country)) || isEdit) &&
                    this.renderForm(data, onDataChange, isEdit)}
            </>
        );
    }

    validateCountry(country: CountryCode): boolean {
        return !this.props.data.some((x) => x.Country == country);
    }

    renderForm(data: CustomerTaxResidencyModel, onDataChange, isEdit: boolean) {
        const { mifirCountryIdentifiers } = this.state;
        return (
            <>
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Client tax resident"}
                    selected={data?.IsTaxResident}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    required={"Please select"}
                    onChange={(v) => {
                        if (!v) {
                            //if the country is Uk, NINO should not be removed even if UK is no longer the tax residency
                            if (data?.Country == CountryCode.GBR) {
                                onDataChange({
                                    PrimaryTaxResidency: null,
                                    IsTaxResident: v,
                                });
                            } else {
                                const obj = {
                                    PrimaryTaxResidency: null,
                                    IsTaxResident: v,
                                    TaxReferenceType: null,
                                    TaxReference: null,
                                };
                                if (
                                    mifirCountryIdentifiers.has(data?.Country) &&
                                    mifirCountryIdentifiers.get(data?.Country)[0].IdentifierType ==
                                        MifirIdentifierType.TaxNumber
                                ) {
                                    obj.TaxReference = data?.TaxReference;
                                }

                                onDataChange(obj);
                            }
                        } else {
                            const obj: any = {
                                IsTaxResident: v,
                            };
                            onDataChange(obj);
                        }
                    }}
                    alwaysValidateOnUpdate={true}
                    validationRules={[
                        {
                            errorMessage:
                                "Either the selected country must be your nationality or you need to be the tax resident of this country (or both)",
                            validator: (v) => {
                                const hasNationality = data?.IsNationality == null || data?.IsNationality;
                                return hasNationality || v == null || v;
                            },
                        },
                        {
                            errorMessage: "Client must have at least one tax residency",
                            validator: (v) => {
                                if (v) {return true;}
                                return (this.props.data || [])
                                    .filter((ele) => ele.Country != data.Country)
                                    .some((ele) => ele.IsTaxResident);
                            },
                        },
                        {
                            errorMessage:
                                "Client mush have controlling person role with specified type for NON UK tax residency",
                            validator: (v) => {
                                // if not mark as tax residency or country is UK, then pass this validation directly
                                if (data?.Country == CountryCode.GBR || !v) {return true;}
                                return this.IsAbleToSetNonUKTaxResidency();
                            },
                        },
                    ]}
                />
                {data?.IsTaxResident && RegulatoryHelper.getTaxReferenceTypeMap(data?.Country) != null && (
                    <SelectEditor
                        placeholder="Please select"
                        options={RegulatoryHelper.getTaxReferenceTypes(data.ActorType, data?.Country)}
                        getText={(v) => {
                            switch (data?.Country) {
                                case CountryCode.USA:
                                    return TaxReferenceTypeMap_USA[v];
                                case CountryCode.GBR:
                                    return TaxReferenceTypeMap_GBR[v];
                                case CountryCode.SGP:
                                    return TaxReferenceTypeMap_SGP[v];
                            }
                        }}
                        selected={data?.TaxReferenceType ? Number(data?.TaxReferenceType) : null}
                        cssWrapper="control-size-430"
                        onChange={(v) => {
                            onDataChange({ TaxReferenceType: Number(v) });
                        }}
                        required={"Please select a tax reference type"}
                        alwaysValidateOnUpdate={false}
                    />
                )}
                {data?.IsTaxResident && (
                    <TextBoxEditor
                        label={"Tax identification number"}
                        textCase="upper"
                        cssWrapper="control-size-430"
                        required={"Please enter"}
                        value={data?.TaxReference}
                        regex={{
                            errorMessage:
                                data.TaxReferenceType == TaxReferenceType.GbrNino
                                    ? "Incorrect NINO format"
                                    : ValidatorContents.AlphaNumericOnly,
                            value:
                                data.TaxReferenceType == TaxReferenceType.GbrNino
                                    ? NationalInsuranceNumber
                                    : Alphanumeric,
                        }}
                        maxLength={50}
                        onChange={(v) => {
                            onDataChange({ TaxReference: v });
                        }}
                    />
                )}

                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"Client nationality"}
                    selected={data?.IsNationality}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    required={"Please select"}
                    onChange={(v) => {
                        if (!v && this.props.data?.length == 1 && isEdit) {
                            this.setState({ showLastDeleteLightbox: true });
                            return;
                        }
                        if (!v) {
                            onDataChange({
                                IsNationality: v,
                                PrimaryCitizenship: null,
                                NationalIdentifier: null,
                                PassportNumber: null,
                            });
                        } else {
                            onDataChange({ IsNationality: v });
                        }
                    }}
                    alwaysValidateOnUpdate={true}
                    validationRules={[
                        {
                            errorMessage:
                                "Either the selected country must be your nationality or you need to be the tax resident of this country (or both)",
                            validator: (v) => {
                                const isTaxResident = data?.IsTaxResident == null || data?.IsTaxResident;

                                return isTaxResident || v == null || v;
                            },
                        },
                    ]}
                />
                {data?.IsNationality && (
                    <RadiosEditor
                        cssWrapper="rg-inline mb-16"
                        label={"Primary citizenship"}
                        selected={data?.PrimaryCitizenship}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        required={"Please select"}
                        onChange={(v) => {
                            if (data?.Country == CountryCode.GBR) {
                                onDataChange({ PrimaryCitizenship: v, TaxReferenceType: TaxReferenceType.GbrNino });
                            } else {
                                onDataChange({ PrimaryCitizenship: v });
                            }
                        }}
                        validationRules={[
                            {
                                errorMessage: "Primary citizenship already exists",
                                validator: (v) => !v || this.validatePrimaryCitizenship(data?.Country),
                            },
                        ]}
                    />
                )}
                {data?.IsNationality &&
                    /* don't display if client is a tax resident in this country and this country has a Tax Reference */
                    !this.countryHasTaxReferenceIdentifier(data.IsTaxResident, data.Country) && (
                        <NationalIdentifierPair
                            data={data}
                            onChange={(v, s) => onDataChange(s)}
                            onRef={(ref) => {
                                this.child = ref;
                            }}
                            mifirCountryIdentifier={
                                this.state.mifirCountryIdentifiers.has(data.Country)
                                    ? this.state.mifirCountryIdentifiers.get(data.Country)
                                    : []
                            }
                            isEdit={isEdit}
                        />
                    )}
            </>
        );
    }

    validatePrimaryCitizenship(country: CountryCode): boolean {
        return this.props.data.findIndex((x) => x.PrimaryCitizenship === true && x.Country != country) == -1;
    }

    onAdd(item: CustomerTaxResidencyModel): boolean {
        if (this.isValid() && (this.child == null || this.child.isValid())) {
            const { data } = this.props;
            data.push(item);
            this.saveData(data);
            return true;
        }
        return false;
    }

    onSave(item: CustomerTaxResidencyModel): boolean {
        if (this.isValid() && (this.child == null || this.child.isValid())) {
            const { data } = this.props;
            const index = data.findIndex((ele) => ele.Country == item.Country);
            data[index] = item;
            this.saveData(data);
            return true;
        }
        return false;
    }

    onRemove(item: number): boolean {
        const { data } = this.props;
        console.log(data);
        data.splice(item, 1);
        this.saveData(data);
        return true;
    }

    saveData(data: Array<CustomerTaxResidencyModel>) {
        ClientRecordAction.saveCutomerTaxResidency(this.props.customerid, data, () => {
            this.getData();
            this.props.reloadTable();
            this.props.needReload();
        });
    }
    loadMifirCountryIdentifier(countryCode: CountryCode) {
        const { mifirCountryIdentifiers } = this.state;
        if (!mifirCountryIdentifiers.has(countryCode)) {
            new MifirCountryIdentifierClient().get(countryCode).onSuccess((data) => {
                mifirCountryIdentifiers.set(countryCode, data);
                this.setState({ mifirCountryIdentifiers: mifirCountryIdentifiers });
            });
        }
    }
    IsAbleToSetNonUKTaxResidency() {
        const { controllingPersonData } = this.props;
        const activeControllingPerson = controllingPersonData.filter((x) => x.AccountRoleStatus == RoleStatus.Active);
        // if no active controlling person role, then no need validate
        if (activeControllingPerson.length == 0) {return true;}
        // all active ciontrolling person role should have a proper type instead of not specifed
        return (
            activeControllingPerson.filter((x) => x.ControllingPersonType == ControllingPersonType.NotSpecified)
                .length == 0
        );
    }
}
